import React from "react";
import type { SpecsModel } from "../../specsModel/SpecsModel";
import { useSpecsModel, useSpecsModelSelector } from "../../SpecsModelContext";
import { SpecsInputField } from "../../SpecsEditorFields/index";

type SpecsModelNumberFieldProps = Omit<
  React.ComponentProps<typeof SpecsInputField>,
  "value" | "onChange" | "type"
> & {
  selector: (model: SpecsModel) => number | null | undefined;
  setValue: (
    model: SpecsModel,
    nextValue: number,
    event:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  fallback?: number;
};

export default function SpecsModelNumberField({
  selector,
  setValue,
  fallback = 0,
  ...props
}: SpecsModelNumberFieldProps) {
  const model = useSpecsModel();
  const numericValue = useSpecsModelSelector(selector);
  const normalizedNumericValue = numericValue ?? fallback;
  const [inputValue, setInputValue] = React.useState(
    String(normalizedNumericValue)
  );

  React.useEffect(() => {
    setInputValue(String(normalizedNumericValue));
  }, [normalizedNumericValue]);

  const handleChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const nextInputValue = event.target.value;
      setInputValue(nextInputValue);

      if (
        nextInputValue === "" ||
        nextInputValue === "-" ||
        nextInputValue === "." ||
        nextInputValue === "-."
      ) {
        return;
      }

      const nextValue = Number(nextInputValue);
      if (!Number.isFinite(nextValue)) {
        return;
      }

      setValue(model, nextValue, event);
    },
    [model, setValue]
  );

  const handleBlur = React.useCallback(
    (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const nextValue = Number(inputValue);
      const committedValue = Number.isFinite(nextValue) ? nextValue : fallback;
      setInputValue(String(committedValue));
      setValue(model, committedValue, event);
      props.onBlur?.(event);
    },
    [fallback, inputValue, model, props, setValue]
  );

  return (
    <SpecsInputField
      {...props}
      type="number"
      value={inputValue}
      onChange={handleChange}
      onBlur={handleBlur}
    />
  );
}
